/**
 * Serialize Client context for the agent API.
 *
 * Which client is on screen is what lets "disconnect this client" and
 * "refresh this client" mean the one being looked at. The catalogue declares
 * current_target for open, disconnect and refresh on client_id, so without
 * this key each of them would ask which client of someone already reading
 * that client's page.
 *
 * @param {object} context mounted page context
 * @return {object} public Client context
 */
export function serializeClientContext(context) {
	const onClientDashboard = context.route?.toLowerCase() === '/client';
	const id = context.client_id ?? context.clientId;
	const name = context.client_name ?? context.clientName;
	return {
		client_id: onClientDashboard && typeof id === 'string' && id
			? id
			: null,
		client_name: onClientDashboard && typeof name === 'string' && name
			? name
			: null,
		// What it is connected through, when the page knows. Left out rather
		// than nulled for a client whose record has not loaded yet, so a
		// half-loaded page never answers "which SSID?" with nothing.
		...connectionAttributes(onClientDashboard, context),
	};
}

/**
 * The SSID, Access Point and Site a client on screen is connected through.
 *
 * Published under the names a looked-up client carries them by, which is
 * what lets one answer serve a client that was named and a client that is
 * simply the one being read.
 *
 * @param {boolean} owns whether this page is about a client
 * @param {object} context mounted page context
 * @return {object} the keys the page actually knows
 */
function connectionAttributes(owns, context) {
	if (!owns) return {};
	const published = {};
	[
		['client_ssid', context.client_ssid],
		['client_ap_name', context.client_ap_name],
		['client_site_name', context.client_site_name],
	].forEach(([key, value]) => {
		if (typeof value === 'string' && value.trim()) {
			published[key] = value.trim();
		}
	});
	return published;
}
